import { useState } from 'react';
import axios from 'axios';
import { Users, ShieldCheck, Check, X, FileText } from 'lucide-react';
import { logError } from '@/utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = { active: 'bg-green-500/15 text-green-400', pending: 'bg-[#EE5A01]/15 text-[#EE5A01]', rejected: 'bg-red-500/15 text-red-400' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function Documents({ userId }) {
  const [docs, setDocs] = useState(null);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/admin/customers/${userId}/documents`, { withCredentials: true }); setDocs(data.documents); } catch (err) { logError('CustomerDocs', err); setDocs([]); }
  };
  if (docs === null) return <button type="button" data-testid={`customer-docs-${userId}`} onClick={load} className="inline-flex items-center gap-1 font-heading text-[10px] uppercase tracking-wider text-[#999] hover:text-[#EE5A01]"><FileText className="w-3 h-3" /> Documents</button>;
  if (docs.length === 0) return <span className="font-body text-[11px] text-[#666]">No documents</span>;
  return (
    <ul className="space-y-1">
      {docs.map((d) => (<li key={d.id}><a href={`${API}/api/admin/documents/${d.id}/file`} target="_blank" rel="noopener noreferrer" data-testid={`admin-doc-${d.id}`} className="font-body text-[11px] text-[#EE5A01] hover:underline">{d.doc_label}{d.expiry_date ? ` · exp ${d.expiry_date}` : ''}</a></li>))}
    </ul>
  );
}

function Actions({ c, onStatus }) {
  const btn = 'inline-flex items-center gap-1 font-heading font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 transition-colors';
  return (
    <div className="flex flex-wrap gap-2">
      {c.status !== 'active' && <button type="button" data-testid={`customer-approve-${c.user_id}`} onClick={() => onStatus(c.user_id, 'active')} className={`${btn} bg-[#EE5A01] text-black hover:bg-[#F17B34]`}><Check className="w-3 h-3" /> Approve</button>}
      {c.status !== 'rejected' && <button type="button" data-testid={`customer-reject-${c.user_id}`} onClick={() => onStatus(c.user_id, 'rejected')} className={`${btn} border border-red-500/40 text-red-400 hover:bg-red-500/10`}><X className="w-3 h-3" /> {c.status === 'active' ? 'Suspend' : 'Reject'}</button>}
    </div>
  );
}

export function CustomersTable({ customers, loading, onStatus }) {
  if (loading) return <p className="font-body text-sm text-[#666] py-10 text-center">Loading customer accounts…</p>;
  if (customers.length === 0) return <div className="text-center py-16 bg-[#111] border border-[#222]"><Users className="w-10 h-10 text-[#333] mx-auto mb-3" /><p className="font-heading text-sm text-[#666]">No customer accounts yet.</p></div>;
  const sorted = [...customers].sort((a, b) => (a.status === 'pending' ? -1 : 0) - (b.status === 'pending' ? -1 : 0));
  return (
    <div data-testid="customers-table" className="space-y-2">
      {sorted.map((c) => (
        <div key={c.user_id} data-testid={`customer-row-${c.user_id}`} className="bg-[#111] border border-[#222] p-4 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_auto] gap-4 items-start">
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm text-[#EEEDE7] truncate">{c.name || '—'} <span className="font-body text-xs text-[#666]">· {c.email}</span></p>
            <p className="font-body text-xs text-[#999] mt-1">{c.mobile || 'no mobile'}{c.company ? ` · ${c.company}` : ''}{c.job_title ? ` · ${c.job_title}` : ''}{c.trade_license ? ` · TL ${c.trade_license}` : ''}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="font-mono text-[10px] uppercase px-2 py-0.5 bg-[#222] text-[#999]">{c.account_type}</span>
              <span data-testid={`customer-status-${c.user_id}`} className={`font-mono text-[10px] uppercase px-2 py-0.5 ${STATUS[c.status] || ''}`}>{c.status}</span>
              {(c.providers || []).map((p) => <span key={p} className="font-mono text-[10px] uppercase px-2 py-0.5 bg-[#1a1a1a] text-[#777]">{p}</span>)}
              {c.uaepass && <span data-testid={`customer-uaepass-${c.user_id}`} className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase px-2 py-0.5 ${c.uaepass.verified ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}><ShieldCheck className="w-3 h-3" /> UAE PASS {c.uaepass.verified ? 'verified' : 'basic'}</span>}
            </div>
          </div>
          <div className="font-body text-xs text-[#999] space-y-1">
            <p>Created {fmt(c.created_at)}</p>
            <p>Last login {fmt(c.last_login_at)}</p>
            {c.uaepass?.idn_masked && <p dir="ltr">EID {c.uaepass.idn_masked} · {c.uaepass.nationality_en}</p>}
          </div>
          <div className="space-y-2">
            <Documents userId={c.user_id} />
          </div>
          <Actions c={c} onStatus={onStatus} />
        </div>
      ))}
    </div>
  );
}
